import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

/* ══════════════════════════════════════
   HERO UTILITIES
══════════════════════════════════════ */

/* Animated keyword typer */
function KeywordTyper({ keywords }) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);
  const [ci, setCi] = useState(0);
  useEffect(() => {
    const w = keywords[idx];
    if (!del && ci < w.length)   { const t = setTimeout(() => setCi(c => c + 1), 60); return () => clearTimeout(t); }
    if (!del && ci === w.length) { const t = setTimeout(() => setDel(true), 1800);    return () => clearTimeout(t); }
    if (del  && ci > 0)          { const t = setTimeout(() => setCi(c => c - 1), 36); return () => clearTimeout(t); }
    if (del  && ci === 0)        { setDel(false); setIdx(i => (i + 1) % keywords.length); }
  }, [ci, del, idx, keywords]);
  useEffect(() => setText(keywords[idx].slice(0, ci)), [ci, idx, keywords]);
  return (
    <span style={{ fontFamily: "monospace" }}>
      {text}
      <span style={{ animation: "pbBlink 1s step-end infinite", color: "#8fa3f8" }}>|</span>
    </span>
  );
}

/* Mini sparkline canvas */
function Sparkline({ data, color, h = 24 }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = c.getContext("2d");
    c.width  = c.offsetWidth * dpr;
    c.height = h * dpr;
    ctx.scale(dpr, dpr);
    const W = c.offsetWidth;
    const mn = Math.min(...data), mx = Math.max(...data), range = mx - mn || 1;
    const pts = data.map((v, i) => [i / (data.length - 1) * W, h - ((v - mn) / range) * (h - 4) - 2]);
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, color + "44"); g.addColorStop(1, color + "00");
    ctx.beginPath(); ctx.moveTo(pts[0][0], h);
    pts.forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.lineTo(W, h); ctx.closePath(); ctx.fillStyle = g; ctx.fill();
    ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
    pts.forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.strokeStyle = color; ctx.lineWidth = 1.8; ctx.stroke();
    const [lx, ly] = pts[pts.length - 1];
    ctx.beginPath(); ctx.arc(lx, ly, 2.8, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
  }, [data, color, h]);
  return <canvas ref={ref} style={{ width: "100%", height: h, display: "block" }} />;
}

/* Animated counter */
function Counter({ end, suffix = "", duration = 2200, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let s = null;
    const f = ts => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / duration, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(f);
    };
    requestAnimationFrame(f);
  }, [active, end, duration]);
  return <>{n}{suffix}</>;
}

/* Particle canvas */
function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize(); window.addEventListener("resize", resize);
    const pts = Array.from({ length: 32 }, () => ({
      x: Math.random() * c.width, y: Math.random() * c.height,
      r: Math.random() * .9 + .3, vx: (Math.random() - .5) * .15, vy: (Math.random() - .5) * .15,
      o: Math.random() * .22 + .04,
    }));
    let raf;
    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x = (p.x + p.vx + c.width)  % c.width;
        p.y = (p.y + p.vy + c.height) % c.height;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(74,95,212,${p.o})`; ctx.fill();
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <canvas ref={ref} style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none", zIndex: 0,
    }} />
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function PunjabPage() {

  const [liveLeads,  setLiveLeads]  = useState(1284);
  const [statsOn,    setStatsOn]    = useState(false);
  const statsRef = useRef(null);

  /* live lead counter */
  useEffect(() => {
    const t = setInterval(() => setLiveLeads(n => n + Math.floor(Math.random() * 2)), 2200);
    return () => clearInterval(t);
  }, []);

  /* stats trigger */
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setStatsOn(true); },
      { threshold: .25 }
    );
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  /* inject hero CSS once */
  useEffect(() => {
    const id = "pb-hero-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = `
      @keyframes pbBlink   { 0%,100%{opacity:1} 50%{opacity:0} }
      @keyframes pbFadeUp  { from{opacity:0;transform:translateY(26px)} to{opacity:1;transform:none} }
      @keyframes pbFloat   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
      @keyframes pbFloatR  { 0%,100%{transform:translateY(0) rotate(0deg)} 45%{transform:translateY(-16px) rotate(.5deg)} }
      @keyframes pbPing    { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.4);opacity:0} }
      @keyframes pbPulse   { 0%,100%{opacity:1} 50%{opacity:.3} }
      @keyframes pbMorph   { 0%,100%{background-position:0 50%} 50%{background-position:100% 50%} }
      @keyframes pbSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
      @keyframes pbShimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
      @keyframes pbBarIn   { from{width:0} }

      .pb-shimmer {
        background: linear-gradient(90deg,#8fa3f8,#6b5ce7,#4f8ef7,#8fa3f8);
        background-size: 260% auto;
        -webkit-background-clip: text; background-clip: text; color: transparent;
        animation: pbShimmer 3.5s linear infinite;
      }
      .pb-outline {
        -webkit-text-stroke: 2.5px #2d3fb5;
        color: transparent; display: block;
      }
      .pb-morph-btn {
        background: linear-gradient(135deg,#1a2472,#2d3fb5,#4a5fd4,#1a2472) !important;
        background-size: 300% 300% !important;
        animation: pbMorph 4s ease infinite !important;
      }
      .pb-float  { animation: pbFloat  7s ease-in-out infinite; }
      .pb-floatr { animation: pbFloatR 9s ease-in-out infinite; }

      /* hero entrance */
      .pb-h1 { animation: pbFadeUp .8s ease .05s forwards; opacity: 0; }
      .pb-h2 { animation: pbFadeUp .8s ease .20s forwards; opacity: 0; }
      .pb-h3 { animation: pbFadeUp .8s ease .35s forwards; opacity: 0; }
      .pb-hp { animation: pbFadeUp .8s ease .50s forwards; opacity: 0; }
      .pb-hb { animation: pbFadeUp .8s ease .65s forwards; opacity: 0; }

      .pb-pill {
        padding: 7px 16px; border-radius: 999px; font-size: 13px; font-weight: 600;
        border: 1px solid rgba(74,95,212,.32); color: #8fa3f8;
        background: rgba(74,95,212,.1); cursor: default; display: inline-block;
        transition: all .3s ease;
      }
      .pb-pill:hover { background:rgba(74,95,212,.22); border-color:rgba(74,95,212,.7); color:#c7d2fe; transform:translateY(-2px); }

      .pb-kw-row { transition: background .25s; }
      .pb-kw-row:hover { background: rgba(74,95,212,.1) !important; }

      .pb-serp-row { transition: background .25s; }
      .pb-serp-row:hover { background: rgba(74,95,212,.09) !important; }

      .pb-stat-cell { transition: background .3s; cursor: default; }
      .pb-stat-cell:hover { background: rgba(45,63,181,.08) !important; }

      .pb-btn-out {
        border: 1px solid rgba(74,95,212,.32); background: transparent;
        color: rgba(255,255,255,.75); font-size: 16px; font-weight: 600;
        padding: 15px 30px; border-radius: 14px; cursor: pointer;
        transition: all .3s;
      }
      .pb-btn-out:hover { border-color:rgba(74,95,212,.75); color:#fff; background:rgba(74,95,212,.12); }

      /* responsive */
      @media (max-width: 1024px) {
        .pb-right-col   { display: none !important; }
        .pb-hero-inner  { grid-template-columns: 1fr !important; }
        .pb-stats-strip { grid-template-columns: repeat(2, 1fr) !important; }
      }
      @media (max-width: 768px) {
        .pb-hero-wrap   { padding: 32px 20px 48px !important; }
        .pb-pills-wrap  { display: none !important; }
        .pb-btn-wrap    { flex-direction: column !important; }
        .pb-btn-wrap button { width: 100% !important; }
        .pb-stats-strip { grid-template-columns: repeat(2, 1fr) !important; }
        .pb-mini-stats  { flex-wrap: wrap; gap: 20px !important; }
      }
    `;
    document.head.appendChild(el);
  }, []);

  /* data */
  const kwRows = [
    { rank: 1, kw: "digital marketing Punjab",      vol: "6.2k/mo", color: "#4ade80", data: [18, 13, 8, 5, 2, 1] },
    { rank: 1, kw: "SEO services Ludhiana",          vol: "3.8k/mo", color: "#4ade80", data: [16, 11, 7, 4, 2, 1] },
    { rank: 2, kw: "Google Ads agency Punjab",       vol: "2.9k/mo", color: "#60a5fa", data: [17, 12, 8, 5, 3, 2] },
    { rank: 2, kw: "social media marketing Mohali",  vol: "2.1k/mo", color: "#60a5fa", data: [15, 10, 7, 4, 3, 2] },
    { rank: 3, kw: "lead generation Amritsar",       vol: "1.7k/mo", color: "#facc15", data: [20, 14, 10, 7, 5, 3] },
    { rank: 4, kw: "performance marketing Punjab",   vol: "1.4k/mo", color: "#facc15", data: [22, 16, 12, 8, 6, 4] },
  ];

  const serpRows = [
    { pos: 1, url: "softcoders.in",   top: true,  title: "SoftCoders — #1 Digital Marketing Punjab",     desc: "SEO, Google Ads & social media for Punjab businesses." },
    { pos: 2, url: "marketer.co",     top: false, title: "Punjab Digital Marketing | Marketer Co",        desc: "Performance marketing services for local businesses." },
    { pos: 3, url: "growthpb.com",    top: false, title: "Digital Growth Agency — Punjab",               desc: "Lead generation and SEO specialists in Punjab." },
  ];

  const cities = ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Mohali", "Bathinda"];

  const services = [
    "SEO Services",
    "Google Ads Management",
    "Social Media Marketing",
    "Performance Marketing",
    "Local SEO",
    "Lead Generation",
  ];

  const faqs = [
    { q: "Why choose SoftCoders for digital marketing in Punjab?",  a: "SoftCoders provides SEO, Google Ads, local SEO, and performance marketing strategies focused on traffic growth and lead generation." },
    { q: "Do you provide local SEO services in Punjab?",            a: "Yes. We optimize Google Business Profiles, local landing pages, citations, and maps visibility." },
    { q: "How long does SEO take in Punjab?",                       a: "SEO generally takes 3 to 6 months depending on competition, website authority, and market demand." },
    { q: "Do you work with local businesses?",                      a: "Yes. We work with startups, manufacturers, schools, healthcare brands, and local businesses across Punjab." },
  ];

  return (
    <div className="bg-[#070B1A] text-white overflow-hidden">

      
  <Helmet>

    {/* PRIMARY SEO */}
    <title>
      Digital Marketing Company in Punjab | SEO & Google Ads Agency | SoftCoders
    </title>

    <meta
      name="description"
      content="SoftCoders is a digital marketing company in Punjab providing SEO services, Google Ads management, local SEO, social media marketing, lead generation, and performance marketing solutions for startups, local businesses, manufacturers, schools, healthcare brands, and enterprises."
    />

    <meta
      name="keywords"
      content="
      digital marketing company in punjab,
      seo company in punjab,
      google ads agency punjab,
      local seo services punjab,
      social media marketing punjab,
      performance marketing agency,
      lead generation company,
      digital marketing agency punjab,
      seo services ludhiana,
      seo company mohali,
      google ads agency amritsar,
      digital marketing jalandhar,
      local business seo,
      technical seo services,
      content marketing company,
      startup marketing agency,
      ecommerce marketing agency,
      healthcare digital marketing,
      school marketing services,
      online marketing company,
      ppc advertising agency,
      semantic seo services,
      website ranking services,
      organic traffic growth,
      google ranking company,
      business growth marketing,
      social media advertising,
      digital growth agency,
      search engine optimization,
      performance marketing punjab
      "
    />

    <meta name="author" content="SoftCoders" />
    <meta name="robots" content="index, follow" />
    <meta name="language" content="English" />
    <meta name="revisit-after" content="2 days" />

    {/* CANONICAL */}
    <link
      rel="canonical"
      href="https://softcoders.in/digital-marketing-punjab"
    />

    {/* OPEN GRAPH */}
    <meta
      property="og:title"
      content="Digital Marketing Company in Punjab | SoftCoders"
    />

    <meta
      property="og:description"
      content="SEO services, Google Ads management, local SEO, social media marketing, and performance marketing solutions in Punjab."
    />

    <meta
      property="og:url"
      content="https://softcoders.in/digital-marketing-punjab"
    />

    <meta property="og:type" content="website" />

    <meta
      property="og:image"
      content="https://softcoders.in/og-punjab-digital-marketing.jpg"
    />

    {/* TWITTER SEO */}
    <meta name="twitter:card" content="summary_large_image" />

    <meta
      name="twitter:title"
      content="Digital Marketing Company in Punjab | SoftCoders"
    />

    <meta
      name="twitter:description"
      content="Professional SEO services, Google Ads, local SEO, and social media marketing solutions in Punjab."
    />

    <meta
      name="twitter:image"
      content="https://softcoders.in/og-punjab-digital-marketing.jpg"
    />

    {/* GEO SEO */}
    <meta name="geo.region" content="IN-PB" />
    <meta name="geo.placename" content="Punjab" />
    <meta name="geo.country" content="India" />

    {/* MOBILE SEO */}
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1"
    />

    <meta
      name="theme-color"
      content="#070B1A"
    />

    {/* SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "SoftCoders",
        url: "https://softcoders.in",
        logo: "https://softcoders.in/logo.png",
        image: "https://softcoders.in/og-punjab-digital-marketing.jpg",
        description:
          "SoftCoders provides SEO services, Google Ads management, social media marketing, local SEO, and performance marketing solutions in Punjab.",
        address: {
          "@type": "PostalAddress",
          addressRegion: "Punjab",
          addressCountry: "India"
        },
        areaServed: {
          "@type": "State",
          name: "Punjab"
        },
        sameAs: [
          "https://www.facebook.com/",
          "https://www.instagram.com/",
          "https://www.linkedin.com/"
        ],
        service: [
          {
            "@type": "Service",
            name: "SEO Services"
          },
          {
            "@type": "Service",
            name: "Google Ads Management"
          },
          {
            "@type": "Service",
            name: "Social Media Marketing"
          },
          {
            "@type": "Service",
            name: "Local SEO"
          },
          {
            "@type": "Service",
            name: "Lead Generation"
          },
          {
            "@type": "Service",
            name: "Performance Marketing"
          }
        ]
      })}
    </script>

    {/* FAQ SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Why choose SoftCoders for digital marketing in Punjab?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "SoftCoders provides SEO, Google Ads, local SEO, and performance marketing strategies focused on traffic growth and lead generation."
            }
          },
          {
            "@type": "Question",
            name: "Do you provide local SEO services in Punjab?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. We optimize Google Business Profiles, local landing pages, citations, and maps visibility."
            }
          },
          {
            "@type": "Question",
            name: "How long does SEO take in Punjab?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "SEO generally takes 3 to 6 months depending on competition, website authority, and market demand."
            }
          },
          {
            "@type": "Question",
            name: "Do you work with local businesses?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. We work with startups, manufacturers, schools, healthcare brands, and local businesses across Punjab."
            }
          }
        ]
      })}
    </script>

  </Helmet>

      {/* ══════════════════════════════════════════════════
          IMPROVED HERO SECTION
      ══════════════════════════════════════════════════ */}
      <section
        className="pb-hero-wrap relative overflow-hidden border-b border-white/5"
        style={{ padding: "72px 60px 60px" }}
      >
        <Particles />

        {/* bg grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          opacity: .027,
          backgroundImage: "linear-gradient(rgba(74,95,212,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(74,95,212,.6) 1px,transparent 1px)",
          backgroundSize: "68px 68px",
        }} />

        {/* diagonal right panel */}
        <div className="absolute top-0 right-0 h-full pointer-events-none" style={{
          width: "52%",
          background: "linear-gradient(140deg,rgba(26,36,114,.48),rgba(45,63,181,.14))",
          clipPath: "polygon(12% 0,100% 0,100% 100%,0% 100%)",
        }} />

        {/* glow orbs */}
        <div className="pb-floatr absolute pointer-events-none" style={{ top: "-60px", right: "-40px", width: 480, height: 480, borderRadius: "50%", opacity: .11, background: "radial-gradient(circle,#2d3fb5,transparent 70%)" }} />
        <div className="pb-float  absolute pointer-events-none" style={{ bottom: "-50px", left: "2%",  width: 320, height: 320, borderRadius: "50%", opacity: .08, background: "radial-gradient(circle,#4a5fd4,transparent 70%)" }} />

        {/* spinning ring */}
        <div className="absolute pointer-events-none" style={{ top: "50%", right: "4%", width: 250, height: 250, borderRadius: "50%", border: "1px solid rgba(74,95,212,.08)", transform: "translateY(-50%)", animation: "pbSpin 45s linear infinite" }} />

        {/* ghost text */}
        <div className="absolute bottom-[-16px] right-[-6px] select-none pointer-events-none" style={{ fontSize: "clamp(80px,13vw,190px)", fontWeight: 900, color: "rgba(45,63,181,.036)", lineHeight: 1 }}>PB</div>

        <div className="relative z-10 max-w-7xl mx-auto">

          {/* top bar */}
          <div className="pb-h1 flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#8fa3f8", animation: "pbPulse 2s infinite" }} />
                <div className="absolute inset-0" style={{ borderRadius: "50%", background: "#8fa3f8", animation: "pbPing 1.5s ease infinite" }} />
              </div>
              <span style={{ color: "#8fa3f8", fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", fontWeight: 600 }}>
                SoftCoders / Punjab
              </span>
            </div>
            <div className="hidden lg:flex items-center gap-4 text-xs text-white/20 tracking-widest uppercase">
              <span>SEO</span><span>·</span><span>Google Ads</span><span>·</span><span>Social Media</span>
            </div>
          </div>

          {/* two-column */}
          <div className="pb-hero-inner" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>

            {/* ── LEFT ── */}
            <div>

              {/* eyebrow */}
              <div className="pb-h1 inline-flex items-center gap-2 mb-6" style={{ padding: "7px 18px", borderRadius: 999, border: "1px solid rgba(74,95,212,.3)", background: "rgba(74,95,212,.1)", fontSize: 13, color: "#8fa3f8", fontWeight: 600 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#8fa3f8", animation: "pbPulse 2s infinite" }} />
                Punjab Digital Marketing Agency
              </div>

              {/* headline */}
              <h1 style={{ fontSize: "clamp(36px,5vw,70px)", fontWeight: 900, lineHeight: .92, letterSpacing: "-2px" }}>
                <span className="pb-h1" style={{ display: "block", color: "#fff" }}>Digital Marketing</span>
                <span className="pb-h2 pb-outline" style={{ fontSize: "clamp(34px,4.8vw,66px)" }}>Services in</span>
                <span className="pb-h3" style={{ display: "block" }}>
                  <span className="pb-shimmer">Punjab.</span>
                </span>
              </h1>

              <p className="pb-hp" style={{ marginTop: 24, color: "#7a8bb5", fontSize: 16, lineHeight: 1.8, maxWidth: 460 }}>
                SoftCoders provides digital marketing services in Punjab — SEO, Google Ads management, local SEO, social media marketing, and performance marketing solutions designed to grow traffic, leads, and revenue.
              </p>

              {/* pills */}
              <div className="pb-pills-wrap pb-hb" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
                {["SEO Services", "Google Ads", "Local SEO", "Social Media", "Lead Generation"].map((p, i) => (
                  <span key={i} className="pb-pill" style={{ animation: `pbFadeUp .6s ease ${.7 + i * .07}s forwards`, opacity: 0 }}>{p}</span>
                ))}
              </div>

              {/* buttons */}
              <div className="pb-btn-wrap pb-hb" style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 32 }}>
                <a
    href="/contact"

                  className="pb-morph-btn"
                  style={{ padding: "15px 30px", borderRadius: 14, fontWeight: 800, fontSize: 16, border: "none", color: "#fff", cursor: "pointer", boxShadow: "0 14px 44px rgba(45,63,181,.45)", transition: "transform .4s,box-shadow .4s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 20px 60px rgba(45,63,181,.62)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 14px 44px rgba(45,63,181,.45)"; }}
                >
                  Get Free Marketing Audit →
                </a>
                <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="pb-btn-out">Talk To Expert </a>
              </div>

              {/* mini stats */}
              <div className="pb-mini-stats pb-hb" style={{ display: "flex", gap: 32, marginTop: 44 }}>
                {[["500%", "Traffic Growth"], ["150+", "Businesses Served"], ["4.8x", "Lead Increase"]].map(([v, l], i) => (
                  <div key={i}>
                    <div style={{ fontSize: 22, fontWeight: 900, background: "linear-gradient(135deg,#8fa3f8,#2d3fb5)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{v}</div>
                    <div style={{ color: "#475569", fontSize: 12, marginTop: 3 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── RIGHT — overlapping dashboard cards ── */}
            <div className="pb-right-col" style={{ position: "relative", height: 540 }}>

              {/* CARD A — Keyword Rankings (back-left) */}
              <div className="pb-floatr" style={{
                position: "absolute", left: 0, top: 36, width: 215, zIndex: 10,
                background: "rgba(10,14,38,.98)", border: "1px solid rgba(74,95,212,.22)",
                borderRadius: 18, overflow: "hidden",
                boxShadow: "0 28px 70px rgba(0,0,0,.65), 0 0 0 1px rgba(74,95,212,.06)",
              }}>
                <div style={{ padding: "11px 12px", borderBottom: "1px solid rgba(255,255,255,.05)", background: "rgba(18,24,56,.55)" }}>
                  <div style={{ fontSize: 9, color: "#4b5563", marginBottom: 2, letterSpacing: ".1em", textTransform: "uppercase" }}>Keyword Rankings</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>Punjab Keywords</div>
                </div>
                <div style={{ padding: "8px 8px" }}>
                  {kwRows.map((k, i) => (
                    <div key={i} className="pb-kw-row" style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 8px", marginBottom: 3, background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.03)", borderRadius: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, background: k.rank <= 2 ? "#2d3fb5" : "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: k.rank <= 2 ? "#fff" : "#7a8bb5" }}>{k.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#c7d2fe", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.kw}</div>
                        <div style={{ fontSize: 9, color: "#4b5563" }}>{k.vol}</div>
                      </div>
                      <div style={{ width: 40, flexShrink: 0 }}>
                        <Sparkline data={k.data} color={k.color} h={20} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CARD B — SERP Results (front-right, main) */}
              <div className="pb-float" style={{
                position: "absolute", right: 0, top: 0, width: 278, zIndex: 20,
                background: "rgba(8,11,30,.99)", border: "1px solid rgba(74,95,212,.22)",
                borderRadius: 18, overflow: "hidden",
                boxShadow: "0 36px 90px rgba(0,0,0,.75), 0 0 0 1px rgba(74,95,212,.07)",
              }}>
                {/* search bar */}
                <div style={{ padding: "11px 13px", borderBottom: "1px solid rgba(255,255,255,.05)", background: "rgba(14,20,52,.7)", display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0, background: "conic-gradient(#ea4335 0 25%,#fbbc05 25% 50%,#34a853 50% 75%,#4285f4 75%)" }} />
                  <div style={{ flex: 1, background: "rgba(255,255,255,.07)", borderRadius: 20, padding: "5px 10px", fontSize: 11, color: "#7a8bb5" }}>
                    <KeywordTyper keywords={["digital marketing Punjab", "SEO services Ludhiana", "Google Ads agency Mohali", "social media marketing Punjab"]} />
                  </div>
                </div>
                {/* result count */}
                <div style={{ padding: "5px 13px", background: "rgba(8,11,30,.5)", borderBottom: "1px solid rgba(255,255,255,.03)", fontSize: 9, color: "#374151", fontFamily: "monospace" }}>
                  About 1,240,000,000 results (0.38 sec)
                </div>

                {/* live leads widget */}
                <div style={{ margin: "10px 10px 6px", padding: "10px 12px", borderRadius: 10, background: "rgba(16,22,56,.75)", border: "1px solid rgba(74,95,212,.22)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, color: "#7a8bb5" }}>Leads Generated Today</span>
                    <span style={{ fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", gap: 4, color: "#4ade80" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4ade80", display: "inline-block", animation: "pbPulse 1.5s infinite" }} />
                      LIVE
                    </span>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", marginTop: 4, letterSpacing: "-1px" }}>
                    {liveLeads.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: "#4ade80", marginTop: 2 }}>↑ 4.8× increase</div>
                </div>

                {/* search results label */}
                <div style={{ padding: "0 10px 4px" }}>
                  <div style={{ fontSize: 9, color: "#4b5563", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 2 }}>Search Results</div>
                  {serpRows.map((r, i) => (
                    <div key={i} className="pb-serp-row" style={{ padding: "9px 10px", borderRadius: 8, marginBottom: 5, position: "relative", overflow: "hidden", background: r.top ? "rgba(74,95,212,.1)" : "rgba(255,255,255,.02)", border: r.top ? "1px solid rgba(74,95,212,.3)" : "1px solid rgba(255,255,255,.04)" }}>
                      {r.top && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1.5, background: "linear-gradient(90deg,#2d3fb5,#4f8ef7)" }} />}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, background: r.top ? "#2d3fb5" : "rgba(255,255,255,.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 900, color: r.top ? "#fff" : "#7a8bb5" }}>{r.pos}</div>
                        <span style={{ fontSize: 9, color: "#475569", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.url}</span>
                        {r.top && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 99, background: "#2d3fb5", color: "#fff", fontWeight: 700, flexShrink: 0 }}>Top Result</span>}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: r.top ? "#c7d2fe" : "#94a3b8", marginBottom: 3, lineHeight: 1.3 }}>{r.title}</div>
                      <div style={{ fontSize: 9, color: "#475569", lineHeight: 1.4 }}>{r.desc}</div>
                    </div>
                  ))}
                </div>

                {/* city coverage bars */}
                <div style={{ padding: "6px 12px 12px", borderTop: "1px solid rgba(255,255,255,.04)" }}>
                  <div style={{ fontSize: 9, color: "#4b5563", marginBottom: 8, letterSpacing: ".08em", textTransform: "uppercase" }}>City Coverage</div>
                  {[["Ludhiana", 95], ["Amritsar", 88], ["Mohali", 82]].map(([city, pct], i) => (
                    <div key={i} style={{ marginBottom: 7 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#64748b", marginBottom: 3 }}>
                        <span>{city}</span><span style={{ color: "#8fa3f8" }}>{pct}%</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,.05)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: "linear-gradient(90deg,#2d3fb5,#4a5fd4)", animation: `pbBarIn .8s ease ${i * .15 + .3}s both` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* BADGE — Google Rank #1 */}
              <div style={{ position: "absolute", top: 4, right: -12, zIndex: 30, background: "rgba(7,9,24,.97)", border: "1px solid rgba(74,95,212,.28)", borderRadius: 14, padding: "12px 18px", backdropFilter: "blur(20px)", animation: "pbFadeUp .7s ease 1s forwards, pbFloat 7s ease-in-out 1.5s infinite", opacity: 0, textAlign: "center", minWidth: 110, boxShadow: "0 14px 44px rgba(0,0,0,.55)" }}>
                <div style={{ fontSize: 9, color: "#4b5563", marginBottom: 4, letterSpacing: ".1em", textTransform: "uppercase" }}>Google Rank</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1 }}>#1 🏆</div>
              </div>

              {/* BADGE — Lead Growth */}
              <div style={{ position: "absolute", bottom: 20, right: -12, zIndex: 30, background: "rgba(7,9,24,.97)", border: "1px solid rgba(74,222,128,.25)", borderRadius: 14, padding: "12px 16px", backdropFilter: "blur(20px)", animation: "pbFadeUp .7s ease 1.2s forwards, pbFloatR 9s ease-in-out 2s infinite", opacity: 0, boxShadow: "0 14px 44px rgba(0,0,0,.55)" }}>
                <div style={{ fontSize: 9, color: "#4b5563", marginBottom: 3, letterSpacing: ".1em", textTransform: "uppercase" }}>Lead Growth</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#4ade80" }}>4.8×</div>
                <div style={{ fontSize: 9, color: "#4b5563", marginTop: 2 }}>avg per client</div>
              </div>

              {/* BADGE — Cities Covered */}
              <div style={{ position: "absolute", bottom: 80, left: -12, zIndex: 30, background: "rgba(7,9,24,.97)", border: "1px solid rgba(74,95,212,.28)", borderRadius: 14, padding: "12px 16px", backdropFilter: "blur(20px)", animation: "pbFadeUp .7s ease 1.4s forwards, pbFloat 8s ease-in-out 2.5s infinite", opacity: 0, boxShadow: "0 14px 44px rgba(0,0,0,.55)" }}>
                <div style={{ fontSize: 9, color: "#4b5563", marginBottom: 3, letterSpacing: ".1em", textTransform: "uppercase" }}>Cities Covered</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#8fa3f8" }}>6+</div>
                <div style={{ fontSize: 9, color: "#4ade80", marginTop: 2 }}>across Punjab</div>
              </div>
            </div>
          </div>

          {/* STATS STRIP */}
          <div ref={statsRef} className="pb-stats-strip" style={{ marginTop: 56, display: "grid", gridTemplateColumns: "repeat(4,1fr)", background: "rgba(10,14,36,.85)", borderRadius: 18, overflow: "hidden", border: "1px solid rgba(74,95,212,.18)" }}>
            {[
              [500, "%", "Traffic Growth"],
              [12,  "M+","Ad Impressions"],
              [150, "+", "Businesses Served"],
              [99,  "%", "Client Satisfaction"],
            ].map(([end, suf, lbl], i) => (
              <div key={i} className="pb-stat-cell" style={{ padding: "24px 22px", borderRight: i < 3 ? "1px solid rgba(255,255,255,.04)" : "none" }}>
                <div style={{ fontSize: 28, fontWeight: 900, background: "linear-gradient(135deg,#8fa3f8,#2d3fb5)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                  <Counter end={end} suffix={suf} active={statsOn} />
                </div>
                <div style={{ color: "#4b5563", fontSize: 12, marginTop: 6 }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* END IMPROVED HERO */}

      {/* ══════════════════════════════════════════════════
          ALL OTHER SECTIONS — UNCHANGED BELOW
      ══════════════════════════════════════════════════ */}

      {/* CONTENT */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <h2 className="text-5xl font-black leading-tight">Best Digital Marketing Company in Punjab</h2>
          <div className="space-y-8 text-slate-400 text-lg leading-9 mt-10">
            <p>SoftCoders is a digital marketing company in Punjab helping businesses improve online visibility, generate qualified leads, increase organic traffic, and scale revenue through SEO, Google Ads, social media marketing, and performance marketing strategies.</p>
            <p>Our digital marketing experts work with startups, SaaS companies, manufacturers, educational institutions, healthcare businesses, eCommerce brands, and local businesses across Punjab.</p>
            <p>We provide technical SEO, local SEO optimization, content marketing, conversion optimization, paid advertising, and growth-focused digital marketing solutions tailored for competitive industries.</p>
            <p>Our SEO services in Punjab include keyword research, on-page optimization, Core Web Vitals improvements, schema markup, authority backlink building, internal linking systems, and semantic content strategies designed for long-term rankings.</p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">SERVICES</div>
            <h2 className="text-5xl font-black mt-6">Digital Marketing Services in Punjab</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">
            {services.map((service, i) => (
              <div key={i} className="rounded-3xl border border-white/10 bg-[#0E142B] p-8 hover:border-indigo-500/30 transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 text-2xl mb-6">→</div>
                <h3 className="text-2xl font-bold">{service}</h3>
                <p className="text-slate-400 mt-5 leading-8">Professional {service.toLowerCase()} solutions designed to improve visibility, generate leads, and grow business revenue in Punjab.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">CITIES WE SERVE</div>
            <h2 className="text-5xl font-black mt-6">Digital Marketing Services Across Punjab</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">
            {cities.map((city, i) => (
              <div key={i} className="rounded-3xl border border-white/10 bg-[#0E142B] p-8 hover:border-indigo-500/30 transition-all duration-300">
                <div className="text-indigo-400 font-semibold">Punjab</div>
                <h3 className="text-3xl font-black mt-4">{city}</h3>
                <p className="text-slate-400 mt-5 leading-8">SEO services, Google Ads, local SEO, and social media marketing services in {city}.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-20">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">FAQ</div>
            <h2 className="text-5xl font-black mt-6">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-3xl border border-white/10 bg-[#0E142B] p-8">
                <h3 className="text-2xl font-bold">{faq.q}</h3>
                <p className="text-slate-400 leading-8 mt-4">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      

    </div>
  );
}